import * as Inovx from '@shared';
import FacilityStore from '../stores/FacilityStore';
const PermissionService = Inovx.Shared.Permission.PermissionService;

const FACILITY_USE_PERMISSION = "Facility.Use";
const MANAGE_FACILITY_PERMISSION = "Facility.Manage";
const MANAGE_KB_PERMISSION = "Facility.ManageKB";
const MODIFY_MEDIA_FILES_PERMISSION = "Facility.ModifyMediaFiles";
const MANAGE_MEDIA_FILES_PERMISSION = "Facility.ManageMediaFiles";
const MODIFY_ASSET_SETS_PERMISSION = "Facility.ModifyAssetSets";
const MANAGE_ASSET_SETS_PERMISSION = "Facility.ManageAssetSets";
const MODIFY_TAGS_PERMISSION = "Facility.ModifyTags";
const MANAGE_TAGS_PERMISSION = "Facility.ManageTags";

export enum KnowledgeBookPermissionLevel {
    None = 0,
    View = 1,
    Modify = 2,
    Manage = 3,
}

const _getRootAncestorTag = (
    tag: IClassificationTag,
    tagMap: ReadonlyMap<string, IClassificationTag>,
) => {
    let currentTag = tag;

    while (currentTag.ParentID) {
        const parentTag = tagMap.get(currentTag.ParentID);
        if (!parentTag) {
            break;
        }

        currentTag = parentTag;
    }

    return currentTag;
}

const _getClassificationTagPermissionLevel = (
    tag: IClassificationTag | undefined,
    tagMap: ReadonlyMap<string, IClassificationTag>,
): KnowledgeBookPermissionLevel => {
    if (!tag) {
        return KnowledgeBookPermissionLevel.None;
    }

    const effectiveTag = tag.ParentID ? _getRootAncestorTag(tag, tagMap) : tag;
    if ((effectiveTag.ManageRoleIDs || []).length > 0) {
        return KnowledgeBookPermissionLevel.Manage;
    }
    if ((effectiveTag.ModifyRoleIDs || []).length > 0) {
        return KnowledgeBookPermissionLevel.Modify;
    }
    if ((effectiveTag.ViewRoleIDs || []).length > 0) {
        return KnowledgeBookPermissionLevel.View;
    }

    return KnowledgeBookPermissionLevel.None;
}

const _getVisibleKnowledgeBookTags = () => {
    const taxonomyDetail = FacilityStore.getVisibleTaxonomyClassificationDetail();
    return taxonomyDetail?.ClassificationTags || [];
}

const _getKnowledgeBookPermissionLevel = (
    knowledgeBook: { ClassificationTags?: string[] } | null | undefined,
    classificationTags: readonly IClassificationTag[],
): KnowledgeBookPermissionLevel => {
    if (!knowledgeBook) {
        return KnowledgeBookPermissionLevel.None;
    }

    const knowledgeBookTagIDs = knowledgeBook.ClassificationTags || [];
    if (knowledgeBookTagIDs.length === 0) {
        return KnowledgeBookPermissionLevel.None;
    }

    const tagMap = new Map(classificationTags.map(tag => [tag.ID, tag] as const));
    return knowledgeBookTagIDs.reduce<KnowledgeBookPermissionLevel>((highest, tagID) => (
        Math.max(highest, _getClassificationTagPermissionLevel(tagMap.get(tagID), tagMap)) as KnowledgeBookPermissionLevel
    ), KnowledgeBookPermissionLevel.None);
}

const _getCurrentKnowledgeBookPermissionLevel = (
    knowledgeBook: { ClassificationTags?: string[] } | null | undefined,
): KnowledgeBookPermissionLevel => {
    if (!knowledgeBook) {
        return KnowledgeBookPermissionLevel.None;
    }

    if (PermissionService.checkPermission(MANAGE_FACILITY_PERMISSION)) {
        return KnowledgeBookPermissionLevel.Manage;
    }

    return _getKnowledgeBookPermissionLevel(knowledgeBook, _getVisibleKnowledgeBookTags());
}

const ComponentHelpers = {

    hasFacilityExplorerPermission: () => {
        return PermissionService.checkPermission(FACILITY_USE_PERMISSION) || PermissionService.checkPermission(MODIFY_ASSET_SETS_PERMISSION) || PermissionService.checkPermission(MANAGE_ASSET_SETS_PERMISSION);
    },

    hasManageContentPermission: () => {
        return PermissionService.checkPermission(MANAGE_KB_PERMISSION) || PermissionService.checkPermission(MANAGE_FACILITY_PERMISSION);
    },
    hasModifyMediaFilesPermission: () => {
        return PermissionService.checkPermission(MODIFY_MEDIA_FILES_PERMISSION) || PermissionService.checkPermission(MANAGE_MEDIA_FILES_PERMISSION) || PermissionService.checkPermission(MANAGE_FACILITY_PERMISSION);
    },
    hasManageMediaFilesPermission: () => {
        return PermissionService.checkPermission(MANAGE_MEDIA_FILES_PERMISSION) || PermissionService.checkPermission(MANAGE_FACILITY_PERMISSION);
    },
    hasModifyFacilityAssetSetsPermission: () => {
        return PermissionService.checkPermission(MODIFY_ASSET_SETS_PERMISSION) || PermissionService.checkPermission(MANAGE_ASSET_SETS_PERMISSION) || PermissionService.checkPermission(MANAGE_FACILITY_PERMISSION);
    },
    hasManageFacilityAssetSetsPermission: () => {
        return PermissionService.checkPermission(MANAGE_ASSET_SETS_PERMISSION) || PermissionService.checkPermission(MANAGE_FACILITY_PERMISSION);
    },
    hasModifyTagsPermission: () => {
        return PermissionService.checkPermission(MODIFY_TAGS_PERMISSION) || PermissionService.checkPermission(MANAGE_TAGS_PERMISSION) || PermissionService.checkPermission(MANAGE_FACILITY_PERMISSION);
    },
    hasManageTagsPermission: () => {
        return PermissionService.checkPermission(MANAGE_TAGS_PERMISSION) || PermissionService.checkPermission(MANAGE_FACILITY_PERMISSION);
    },
    hasFacilityManagePermission: () => {
        return PermissionService.checkPermission(MANAGE_FACILITY_PERMISSION);
    },
    getClassificationTagPermissionLevel: (
        tag: IClassificationTag | undefined,
        classificationTags: readonly IClassificationTag[] = _getVisibleKnowledgeBookTags(),
    ): KnowledgeBookPermissionLevel => {
        if (!tag) {
            return KnowledgeBookPermissionLevel.None;
        }

        if (PermissionService.checkPermission(MANAGE_FACILITY_PERMISSION)) {
            return KnowledgeBookPermissionLevel.Manage;
        }

        const tagMap = new Map(classificationTags.map(item => [item.ID, item] as const));
        return _getClassificationTagPermissionLevel(tag, tagMap);
    },
    getKnowledgeBookPermissionLevel: (
        knowledgeBook: { ClassificationTags?: string[] } | null | undefined,
    ): KnowledgeBookPermissionLevel => {
        return _getCurrentKnowledgeBookPermissionLevel(knowledgeBook);
    },
    hasViewKnowledgeBookPermission: (knowledgeBook: { ClassificationTags?: string[] } | null | undefined) => {
        return _getCurrentKnowledgeBookPermissionLevel(knowledgeBook) >= KnowledgeBookPermissionLevel.View;
    },
    hasModifyKnowledgeBookPermission: (knowledgeBook: { ClassificationTags?: string[] } | null | undefined) => {
        return _getCurrentKnowledgeBookPermissionLevel(knowledgeBook) >= KnowledgeBookPermissionLevel.Modify;
    },
    hasManageKnowledgeBookPermission: (knowledgeBook: { ClassificationTags?: string[] } | null | undefined) => {
        return _getCurrentKnowledgeBookPermissionLevel(knowledgeBook) >= KnowledgeBookPermissionLevel.Manage;
    },
    hasAnyModifyKnowledgeBookPermission: () => {
        if (PermissionService.checkPermission(MANAGE_FACILITY_PERMISSION)) {
            return true;
        }

        const classificationTags = _getVisibleKnowledgeBookTags();
        const tagMap = new Map(classificationTags.map(tag => [tag.ID, tag] as const));
        for (const tag of classificationTags) {
            if (_getClassificationTagPermissionLevel(tag, tagMap) >= KnowledgeBookPermissionLevel.Modify) {
                return true;
            }
        }

        return false;
    },

}

export default ComponentHelpers;
